import { Link } from "@tanstack/react-router";
import { Facebook, Instagram, Linkedin } from "lucide-react";

import { BrandLogo } from "@/components/brand-logo";
import { languages, useLanguage, type LanguageCode } from "@/lib/i18n";
import { siteInfo, socialProfiles } from "@/lib/site-info";

const socialIcons = { Instagram, Facebook, LinkedIn: Linkedin };
const socialLinks = socialProfiles.map((profile) => ({
  ...profile,
  icon: socialIcons[profile.label],
}));

export function SiteFooter() {
  const { language, setLanguage, t } = useLanguage();

  function openCookieSettings() {
    if (window.ltBanner?.show) {
      window.ltBanner.show();
    } else if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", () => window.ltBanner?.show?.(), {
        once: true,
      });
    }
  }

  return (
    <footer className="mt-16 border-t border-border bg-surface md:mt-24">
      <div className="container-page py-12 md:py-16">
        <div className="grid grid-cols-2 gap-x-6 gap-y-10 md:grid-cols-2 lg:grid-cols-5">
          <div className="col-span-2 lg:col-span-1">
            <Link
              to="/"
              aria-label="Begner Startseite"
              className="inline-flex rounded-lg focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <BrandLogo />
            </Link>
            <p className="mt-4 max-w-xs text-sm leading-relaxed text-muted-foreground">
              {t.footer.description}
            </p>
            <div
              className="mt-5 flex items-center gap-2"
              role="group"
              aria-label="Begner Social Media"
            >
              {socialLinks.map(({ label, href, icon: Icon }) => (
                <a
                  key={label}
                  href={href}
                  target="_blank"
                  rel="noreferrer"
                  aria-label={`Begner auf ${label}`}
                  title={label}
                  className="inline-flex size-9 items-center justify-center rounded-lg border border-border bg-background text-muted-foreground transition-colors hover:border-brand/50 hover:bg-brand-soft hover:text-brand-deep focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                >
                  <Icon className="size-4" aria-hidden="true" />
                </a>
              ))}
            </div>
          </div>

          {t.footer.columns.map((col) => (
            <div key={col.title} className="min-w-0">
              <h3 className="text-sm font-semibold text-foreground">{col.title}</h3>
              <ul className="mt-4 space-y-3">
                {col.links.map((link) => (
                  <li key={link.label}>
                    {link.action === "cookie-settings" ? (
                      <button
                        type="button"
                        onClick={openCookieSettings}
                        className="inline-flex min-h-10 items-center text-left text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {link.label}
                      </button>
                    ) : (
                      <Link
                        to={link.to}
                        hash={link.hash}
                        search={link.search}
                        className="inline-flex min-h-10 items-center text-sm text-muted-foreground transition-colors hover:text-primary"
                      >
                        {link.label}
                      </Link>
                    )}
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>

        <div className="mt-12 flex flex-col items-start justify-between gap-4 border-t border-border pt-6 text-xs text-muted-foreground md:flex-row md:items-center">
          <p>
            © {new Date().getFullYear()} {siteInfo.legalName}. {t.footer.copyright}
          </p>
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-medium text-foreground">{t.footer.languageLabel}</span>
            {languages.map((item) => {
              const isActive = item.code === language;

              return (
                <button
                  key={item.code}
                  type="button"
                  aria-label={`${item.label} – ${item.name}`}
                  aria-pressed={isActive}
                  onClick={() => setLanguage(item.code as LanguageCode)}
                  className={`grid size-10 place-items-center rounded-md transition-colors hover:bg-accent ${
                    isActive ? "font-medium text-foreground" : ""
                  }`}
                >
                  {item.label}
                </button>
              );
            })}
          </div>
        </div>
      </div>
    </footer>
  );
}
